/**
 * 服务端接口封装（与 apps/server 对齐）：
 *   POST /api/auth/login | register { email, password } → { token, user }
 *   GET  /api/works/:id                 → Work 行，含 doc
 *   POST /api/works { title, doc }      → { id, ... }
 *   PUT  /api/works/:id  <Work JSON>    → Work 行
 *   POST /api/works/:id/publish         → { jobId, state }
 *   GET  /api/works/:id/publish/:jobId  → { state, result: { slug } | null, failedReason }
 *   POST /api/assets/sign { filename, mime, size, hash } → { exists, key, url, uploadUrl | null }
 *   GET  /api/templates                 → { items: TemplateSummary[] }（公开）
 *   POST /api/works { templateId, vars, title? } → 从模板创建（见 docs/TEMPLATES.md）
 */
import type { Work } from '@h5/schema';
import { getToken, setToken } from './auth';

const BASE = '/api';

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(BASE + path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  const text = await res.text();
  let body: unknown = text;
  try { body = text ? JSON.parse(text) : null; } catch { /* 非 JSON 原样返回 */ }
  if (!res.ok) {
    if (res.status === 401) setToken(null);
    const msg = (body as { message?: string } | null)?.message ?? `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, msg);
  }
  return body as T;
}

export interface AuthResult { token: string; user: { id: string; email: string } }
export interface PublishStatus {
  jobId: string;
  state: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed' | 'queued' | string;
  result: { slug: string; docUrl: string } | null;
  failedReason: string | null;
}

export interface TemplateVar { key: string; label: string; default: string; placeholder?: string }
export interface TemplateSummary { id: string; title: string; description: string; cover?: string | null; vars: TemplateVar[] }

export const api = {
  /** 模板列表（公开接口，无需登录） */
  async listTemplates(): Promise<TemplateSummary[]> {
    const res = await request<{ items?: TemplateSummary[] } | TemplateSummary[]>('/templates');
    return Array.isArray(res) ? res : (res.items ?? []);
  },
  /** 从模板创建作品：服务端做 {{key}} 替换后返回新作品行（含 doc） */
  createWorkFromTemplate(templateId: string, vars: Record<string, string>, title?: string): Promise<{ id: string; doc?: unknown }> {
    return request('/works', { method: 'POST', body: JSON.stringify({ templateId, vars, ...(title ? { title } : {}) }) });
  },
  login(body: { email: string; password: string }): Promise<AuthResult> {
    return request('/auth/login', { method: 'POST', body: JSON.stringify(body) });
  },
  register(body: { email: string; password: string }): Promise<AuthResult> {
    return request('/auth/register', { method: 'POST', body: JSON.stringify(body) });
  },
  async getWork(id: string): Promise<unknown> {
    const res = await request<{ doc?: unknown }>(`/works/${encodeURIComponent(id)}`);
    return res && typeof res === 'object' && 'doc' in res ? res.doc : res;
  },
  createWork(doc: Work): Promise<{ id: string }> {
    return request('/works', { method: 'POST', body: JSON.stringify({ doc, title: doc.title }) });
  },
  async saveWork(id: string, doc: Work): Promise<void> {
    await request(`/works/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(doc) });
  },
  enqueuePublish(id: string): Promise<{ jobId: string }> {
    return request(`/works/${encodeURIComponent(id)}/publish`, { method: 'POST' });
  },
  publishStatus(id: string, jobId: string): Promise<PublishStatus> {
    return request(`/works/${encodeURIComponent(id)}/publish/${encodeURIComponent(jobId)}`);
  },
  signAsset(body: { filename: string; mime: string; size: number; hash: string }) {
    return request<{ exists: boolean; key: string; url: string; uploadUrl: string | null }>('/assets/sign', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
};

/** 发布：入队后轮询，成功返回发布页 URL。 */
export async function publish(id: string, opts: { timeoutMs?: number; intervalMs?: number } = {}): Promise<{ url: string; slug: string }> {
  const { timeoutMs = 60_000, intervalMs = 1000 } = opts;
  const { jobId } = await api.enqueuePublish(id);
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const st = await api.publishStatus(id, jobId);
    if (st.state === 'completed' && st.result) {
      const base = (import.meta.env.VITE_PUBLIC_URL as string | undefined) ?? window.location.origin;
      return { slug: st.result.slug, url: `${base.replace(/\/+$/, '')}/p/${st.result.slug}` };
    }
    if (st.state === 'failed') throw new ApiError(500, st.failedReason ?? '发布任务失败');
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new ApiError(504, '发布超时，请稍后在作品列表查看结果');
}

async function sha256Hex(file: File): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** 直传文件：算 hash → 签名（命中去重则直接返回）→ PUT。返回可访问 URL。 */
export async function uploadAsset(file: File): Promise<string> {
  const hash = (await sha256Hex(file)).slice(0, 16);
  const sign = await api.signAsset({
    filename: file.name,
    mime: file.type || 'application/octet-stream',
    size: file.size,
    hash,
  });
  if (sign.exists || !sign.uploadUrl) return sign.url;
  const res = await fetch(sign.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!res.ok) throw new ApiError(res.status, `上传失败 ${res.status}`);
  return sign.url;
}

// ---------- localStorage 兜底 ----------
const LS_PREFIX = 'h5:work:';
export const localStore = {
  save(id: string, doc: Work) {
    try { localStorage.setItem(LS_PREFIX + id, JSON.stringify(doc)); localStorage.setItem('h5:last', id); } catch { /* 容量满等，忽略 */ }
  },
  load(id: string): unknown | null {
    try { const s = localStorage.getItem(LS_PREFIX + id); return s ? JSON.parse(s) : null; } catch { return null; }
  },
  lastId(): string | null {
    try { return localStorage.getItem('h5:last'); } catch { return null; }
  },
};
